import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@shared/constants/index.ts';
import { API_BASE, reportUnauthenticated } from './client.ts';

/**
 * The help assistant's transport: POST /help/chat, answered as Server-Sent
 * Events so the first words are on screen while the rest generate.
 *
 * Deliberately not routed through `apiRequest` — that helper parses one JSON
 * envelope, and this response is a stream — but it keeps the same contract:
 * cookie session, CSRF header echoed from the readable cookie, and the
 * error-envelope shape on any non-200.
 */

export interface HelpMessage {
  role: 'user' | 'assistant';
  content: string;
}

/** Why a turn produced no (complete) answer — the panel words each differently. */
export type HelpFailureKind = 'rateLimited' | 'unavailable' | 'error' | 'providerBudget' | 'costPolicy';

export type HelpChatResult = { ok: true; text: string } | { ok: false; kind: HelpFailureKind; partialText: string };

function csrfToken(): string | null {
  for (const part of document.cookie.split(';')) {
    const index = part.indexOf('=');
    if (index === -1) continue;
    if (part.slice(0, index).trim() === CSRF_COOKIE_NAME) return part.slice(index + 1).trim();
  }
  return null;
}

/** One `event:`/`data:` block of an SSE stream, parsed. */
function parseSseBlock(block: string): { event: string; data: string } | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }
  if (data.length === 0) return null;
  return { event, data: data.join('\n') };
}

/**
 * Sends one turn and streams the answer back through `onDelta`, resolving
 * once the turn is over either way. Aborting the signal resolves as a plain
 * `error` with whatever text had arrived — the caller decides what to keep.
 */
export async function streamHelpChat(
  body: { messages: HelpMessage[]; language: 'en' | 'es'; platform?: 'web' | 'mobile'; page?: string },
  onDelta: (text: string) => void,
  signal?: AbortSignal,
): Promise<HelpChatResult> {
  let text = '';
  const failed = (kind: HelpFailureKind): HelpChatResult => ({ ok: false, kind, partialText: text });

  let response: Response;
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json', accept: 'text/event-stream' };
    const csrf = csrfToken();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
    response = await fetch(`${API_BASE}/help/chat`, {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch {
    // Aborted or offline — either way there is nothing more coming.
    return failed('error');
  }

  if (!response.ok || !response.body) {
    // The same session-death handling every apiRequest 401 gets: the app
    // flips to sign-in rather than leaving a signed-in shell around a
    // session that is gone.
    if (response.status === 401) reportUnauthenticated();
    return failed(response.status === 429 ? 'rateLimited' : 'unavailable');
  }

  // The stream arrives in transport chunks that need not align with event
  // boundaries, so blocks are cut at the blank line the protocol defines and
  // the remainder waits in the buffer for its other half.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // A chunk-final CR is withheld from normalization until the next chunk
  // says whether it was a lone CR or the first half of a split CRLF.
  let pendingCr = false;
  let done = false;
  let errored: HelpFailureKind | null = null;

  const drainBlocks = () => {
    for (;;) {
      const boundary = buffer.indexOf('\n\n');
      if (boundary === -1) break;
      const block = parseSseBlock(buffer.slice(0, boundary));
      buffer = buffer.slice(boundary + 2);
      if (!block) continue;

      if (block.event === 'delta') {
        const payload = JSON.parse(block.data) as { text?: string };
        if (payload.text) {
          text += payload.text;
          onDelta(payload.text);
        }
      } else if (block.event === 'done') {
        done = true;
      } else if (block.event === 'error') {
        const payload = JSON.parse(block.data) as { retryable?: boolean; code?: string };
        errored =
          payload.code === 'provider_budget'
            ? 'providerBudget'
            : payload.code === 'cost_policy'
              ? 'costPolicy'
              : payload.retryable
                ? 'error'
                : 'unavailable';
      }
    }
  };

  try {
    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) {
        if (pendingCr) {
          buffer += '\n';
          drainBlocks();
        }
        break;
      }
      // Line-ending-normalized: our Worker writes LF, but SSE permits CRLF
      // and lone CR too, and an intermediary could introduce either.
      let incoming: string = (pendingCr ? '\r' : '') + decoder.decode(chunk.value, { stream: true });
      pendingCr = incoming.endsWith('\r');
      if (pendingCr) incoming = incoming.slice(0, -1);
      buffer += incoming.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      drainBlocks();
    }
  } catch {
    return failed('error');
  }

  if (errored) return failed(errored);
  if (!done) return failed('error');
  // An old server could close a wordless turn with `done`; an empty answer
  // must not be stored as an assistant message the server would later reject.
  if (!text.trim()) return failed('error');
  return { ok: true, text };
}
